import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import { ConfigProvider, DatePicker } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import 'react-toastify/dist/ReactToastify.css';
import '../../assets/scss/lapdat.scss';
import { useSelector, useDispatch } from 'react-redux';
import { postDangKiLapDatTaiNha } from '../../redux/category/action';
import { CLEAR_DANG_KY_LAP_DAT } from '../../redux/type';

dayjs.locale('vi');

const schema = yup.object({
  MA_DON_HANG_SHOPEE: yup.string().required('Vui lòng nhập mã đơn hàng Shopee').min(6, 'Mã đơn hàng không hợp lệ'),
  HO_TEN: yup.string().required('Vui lòng nhập họ và tên').min(2, 'Họ tên phải có ít nhất 2 ký tự'),
  SO_DIEN_THOAI: yup
    .string()
    .required('Vui lòng nhập số điện thoại')
    .matches(/^(0[3|5|7|8|9])+([0-9]{8})$/, 'Số điện thoại không hợp lệ'),
  TEN_SAN_PHAM: yup.string().required('Vui lòng nhập tên sản phẩm'),
  DIA_CHI_LAP_DAT: yup.string().required('Vui lòng nhập địa chỉ lắp đặt'),
  THOI_GIAN_HEN: yup.date().required('Vui lòng chọn thời gian hẹn'),
  LOAI_DICH_VU: yup.string().required('Vui lòng chọn ít nhất một loại dịch vụ'),
  GHI_CHU: yup.string().optional(),
});

const SERVICE_OPTIONS = [
  {
    value: 'Lắp đặt sản phẩm',
    label: 'Lắp đặt sản phẩm',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path
          d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    value: 'Vệ sinh - Bảo dưỡng sản phẩm',
    label: 'Vệ sinh - Bảo dưỡng sản phẩm',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3z" stroke="currentColor" stroke-width="1.6" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    ),
  },
];

const FieldError = ({ message }) =>
  message ? (
    <span className="ldtn-error" role="alert">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {message}
    </span>
  ) : null;

const LapDatTaiNha = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const resultkichhoat = useSelector((store) => store.categories.getKichHoatBaoHanh);
  const dispatch = useDispatch();
  const defaultValues = {
    MA_DON_HANG_SHOPEE: '',
    HO_TEN: '',
    SO_DIEN_THOAI: '',
    TEN_SAN_PHAM: '',
    DIA_CHI_LAP_DAT: '',
    THOI_GIAN_HEN: null,
    LOAI_DICH_VU: '',
    GHI_CHU: '',
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const selectedServices = (watch('LOAI_DICH_VU') || '').split(',').filter(Boolean);

  const toggleService = (value) => {
    const next = selectedServices.includes(value) ? selectedServices.filter((v) => v !== value) : [...selectedServices, value];

    setValue('LOAI_DICH_VU', next.join(','), {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (resultkichhoat) {
      if (resultkichhoat.Success) {
        reset(defaultValues);
        setIsSuccess(true);
        dispatch({
          type: CLEAR_DANG_KY_LAP_DAT,
        });
      } else {
        toast.error(resultkichhoat.Message, {
          position: 'top-right',
        });
      }
      setIsSubmitting(false);
    }
  }, [resultkichhoat]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const payload = {
      ...data,
      THOI_GIAN_HEN: dayjs(data.THOI_GIAN_HEN).format('DD/MM/YYYY HH:mm'),
    };

    await dispatch(postDangKiLapDatTaiNha(payload));
  };

  const handleGoHome = () => {
    dispatch({
      type: CLEAR_DANG_KY_LAP_DAT,
    });

    window.location.href = '/';
  };

  if (isSuccess) {
    return (
      <div className="ldtn-success">
        <div className="ldtn-success__icon">✓</div>

        <h2>Đăng ký thành công</h2>

        <p>Chúng tôi đã nhận được yêu cầu đăng ký lắp đặt của Quý khách. Nhân viên kỹ thuật sẽ liên hệ xác nhận lịch hẹn trong thời gian sớm nhất.</p>

        <button className="ldtn-submit" onClick={handleGoHome}>
          Về trang chủ
        </button>
      </div>
    );
  }

  return (
    <div className="ldtn-wrapper">
      <ToastContainer />

      {/* ── Header ── */}
      <header className="ldtn-header">
        <div className="ldtn-header__bg" aria-hidden="true" />
        <div className="ldtn-header__content">
          <div className="ldtn-header__brand">
            <div className="ldtn-header__icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="14" fill="white" fillOpacity="0.15" />
                <path
                  d="M24 10L38 18V30C38 36 31 41 24 44C17 41 10 36 10 30V18L24 10Z"
                  fill="white"
                  fillOpacity="0.25"
                  stroke="white"
                  strokeWidth="1.5"
                />
                <path d="M18 24L22 28L30 20" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <p className="ldtn-header__eyebrow">Giga Dịch Vụ Tại Nhà</p>
              <h1 className="ldtn-header__title">Đăng ký lắp đặt</h1>
            </div>
          </div>
          <h3 className="ldtn-header__note mb-2">Cảm ơn Quý khách đã tin tưởng dịch vụ của chúng tôi ✦</h3>
          <p className="ldtn-header__sub">Vui lòng điền đầy đủ thông tin bên dưới để đội ngũ kỹ thuật liên hệ và sắp xếp lịch hẹn nhanh chóng.</p>
        </div>
      </header>

      {/* ── Form Card ── */}
      <div className="ldtn-card">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Section: Thông tin đơn hàng */}
          <div className="ldtn-section">
            <h2 className="ldtn-section__title">
              <span className="ldtn-section__num">01</span>
              Thông tin sản phẩm
            </h2>

            <div className="ldtn-grid">
              {/* Mã đơn hàng */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label ldtn-label--required" htmlFor="MA_DON_HANG_SHOPEE">
                  Mã đơn hàng
                </label>
                <div className="ldtn-input-wrap ldtn-input-wrap--icon">
                  <svg className="ldtn-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M9 12h6M9 8h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                  <input
                    id="MA_DON_HANG_SHOPEE"
                    {...register('MA_DON_HANG_SHOPEE')}
                    className={`ldtn-input ${errors.MA_DON_HANG_SHOPEE ? 'ldtn-input--error' : ''}`}
                    placeholder="VD: 250612XXXXXXXX"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
                <FieldError message={errors.MA_DON_HANG_SHOPEE?.message} />
                <span className="ldtn-hint">Nhập mã đơn hàng đã mua</span>
              </div>

              {/* Tên sản phẩm */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label ldtn-label--required" htmlFor="TEN_SAN_PHAM">
                  Tên sản phẩm cần lắp đặt / bảo dưỡng
                </label>
                <input
                  id="TEN_SAN_PHAM"
                  {...register('TEN_SAN_PHAM')}
                  className={`ldtn-input ${errors.TEN_SAN_PHAM ? 'ldtn-input--error' : ''}`}
                  placeholder="VD: T30C, T90 Pro Omni, ..."
                />
                <FieldError message={errors.TEN_SAN_PHAM?.message} />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="ldtn-divider" />

          {/* Section: Thông tin khách hàng */}
          <div className="ldtn-section">
            <h2 className="ldtn-section__title">
              <span className="ldtn-section__num">02</span>
              Thông tin khách hàng
            </h2>

            <div className="ldtn-grid">
              {/* Họ tên */}
              <div className="ldtn-field">
                <label className="ldtn-label ldtn-label--required" htmlFor="HO_TEN">
                  Họ và tên
                </label>
                <input
                  id="HO_TEN"
                  {...register('HO_TEN')}
                  className={`ldtn-input ${errors.HO_TEN ? 'ldtn-input--error' : ''}`}
                  placeholder="Nguyễn Văn A"
                  autoComplete="name"
                />
                <FieldError message={errors.HO_TEN?.message} />
              </div>

              {/* SĐT */}
              <div className="ldtn-field">
                <label className="ldtn-label ldtn-label--required" htmlFor="SO_DIEN_THOAI">
                  Số điện thoại liên hệ
                </label>
                <div className="ldtn-input-wrap ldtn-input-wrap--icon">
                  <svg className="ldtn-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  <input
                    id="SO_DIEN_THOAI"
                    {...register('SO_DIEN_THOAI')}
                    className={`ldtn-input ${errors.SO_DIEN_THOAI ? 'ldtn-input--error' : ''}`}
                    placeholder="0912 345 678"
                    type="tel"
                    maxLength={10}
                    autoComplete="tel"
                  />
                </div>
                <FieldError message={errors.SO_DIEN_THOAI?.message} />
              </div>

              {/* Địa chỉ */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label ldtn-label--required" htmlFor="DIA_CHI_LAP_DAT">
                  Địa chỉ lắp đặt
                </label>
                <textarea
                  id="DIA_CHI_LAP_DAT"
                  {...register('DIA_CHI_LAP_DAT')}
                  className={`ldtn-input ldtn-textarea ${errors.DIA_CHI_LAP_DAT ? 'ldtn-input--error' : ''}`}
                  placeholder="Số nhà, đường/phố, phường/xã, quận/huyện, tỉnh/thành phố"
                  rows={3}
                />
                <FieldError message={errors.DIA_CHI_LAP_DAT?.message} />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="ldtn-divider" />

          {/* Section: Thông tin lịch hẹn */}
          <div className="ldtn-section">
            <h2 className="ldtn-section__title">
              <span className="ldtn-section__num">03</span>
              Lịch hẹn &amp; Dịch vụ
            </h2>

            <div className="ldtn-grid">
              {/* Thời gian */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label ldtn-label--required" htmlFor="THOI_GIAN_HEN">
                  Thời gian hẹn mong muốn
                </label>
                <div className="ldtn-input-wrap ldtn-input-wrap--icon">
                  <svg className="ldtn-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M8 2v4M16 2v4M3 10h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <Controller
                    name="THOI_GIAN_HEN"
                    control={control}
                    render={({ field }) => (
                      <ConfigProvider locale={viVN}>
                        <DatePicker
                          showTime={{ format: 'HH:mm' }}
                          format="DD/MM/YYYY - HH:mm"
                          placeholder="Chọn ngày và giờ hẹn"
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          style={{ width: '100%' }}
                        />
                      </ConfigProvider>
                    )}
                  />
                </div>
                <FieldError message={errors.THOI_GIAN_HEN?.message} />
                <i className="ldtn-hint">Thời gian thực tế có thể điều chỉnh theo lịch kỹ thuật viên</i>
              </div>

              {/* Loại dịch vụ */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label ldtn-label--required">Loại dịch vụ cần thực hiện</label>
                <div className="ldtn-service-group">
                  {SERVICE_OPTIONS.map((opt) => {
                    const checked = selectedServices.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => toggleService(opt.value)}
                        className={`ldtn-service-card ${checked ? 'ldtn-service-card--checked' : ''}`}
                      >
                        <span className="ldtn-service-card__icon">{opt.icon}</span>
                        <span className="ldtn-service-card__label">{opt.label}</span>
                        <span className="ldtn-service-card__check" aria-hidden="true">
                          {checked && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <FieldError message={errors.LOAI_DICH_VU?.message} />
              </div>

              {/* Ghi chú */}
              <div className="ldtn-field ldtn-field--full">
                <label className="ldtn-label" htmlFor="GHI_CHU">
                  Ghi chú thêm
                </label>
                <textarea
                  id="GHI_CHU"
                  {...register('GHI_CHU')}
                  className="ldtn-input ldtn-textarea"
                  placeholder="(Vui lòng cung cấp các thông tin bổ sung nếu có như vị trí lắp đặt, yêu cầu đặc biệt, khung giờ ưu tiên, hình ảnh hiện trạng,…"
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Notice */}
          <div className="ldtn-notice">
            <div className="ldtn-notice__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <ul className="ldtn-notice__list">
              <li>Sau khi nhận được thông tin, kỹ thuật viên sẽ liên hệ xác nhận lịch hẹn.</li>
              <li>Thời gian phục vụ có thể điều chỉnh theo khu vực và lịch làm việc.</li>
              <li>Vui lòng giữ điện thoại để được hỗ trợ nhanh nhất.</li>
            </ul>
          </div>

          {/* Submit */}
          <button type="submit" className={`ldtn-submit ${isSubmitting ? 'ldtn-submit--success' : ''}`} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="ldtn-spinner" aria-hidden="true" />
                Đang gửi...
              </>
            ) : (
              <>
                Xác nhận đăng ký
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LapDatTaiNha;
